<template>
  <div class="station-schedule-row optimiser-row-container station-optimiser-sheet-draggable" @click="onClick" data-type="sheet" :data-sheetid="sheet.id">
    <div class="optimiser-row-highlight-bar" :style="{'background-color': sideBarColour}">
    </div>
    <div class="optimiser-row-contents" v-if="sheet">

      <div class="optimiser-row-thumbnail">
        <img class="thumbnail" height="112px" :src="sheet.thumbnailImageData">
      </div>
      <div class="optimiser-row-details">
        <div class="row no-gutters">
          <div class="col-8">
            <h1>{{sheet.stockName}}</h1>
          </div>
          <div class="col-4 text-right">
            <h1 :class="{'offcut-size': sheet.glassLevelIsOffcut}">{{sheet.height}} x {{sheet.width}}</h1>
          </div>
        </div>

        <div class="row no-gutters">
          <div class="col-6 station-schedule-row-label-area station-schedule-row-truncate">
            <LabelBadge class="text-uppercase" :labelClass="`label-optimiser-sheet-status-${sheet.status}`">{{formatLookupName(sheet.status, OptimiserSheetStatusTypeLookup)}}</LabelBadge>
          </div>
          <div class="col-6 station-schedule-row-truncate text-right">
            <i :class="['fas', 'fa-clock', 'mr-2', 'fa-fw', {'text-danger': isOverdue}]" v-if="dueDate"></i>
            <span class="station-schedule-row-highlight" v-if="!isOverdue && dueDate">{{dueDate | formatTime}}</span>
            <span class="station-schedule-row-highlight station-schedule-row-highlight-overdue text-danger" v-if="isOverdue && dueDate">NOW</span>
          </div>
        </div>

        <div class="row no-gutters">
          <div class="col-3" v-if="isStandardSheet">
            <i class="far fa-layer-group mr-2 fa-fw"></i> <span class="station-schedule-row-highlight">{{sheet.panelCount}} Panel{{displayCountPlural(sheet.panelCount)}}</span>
          </div>
          <div class="col-3" v-if="isStandardSheet">
            <i class="far fa-recycle mr-2 fa-fw"></i> <span class="station-schedule-row-highlight">{{sheet.offcutCount}} Offcut{{displayCountPlural(sheet.offcutCount)}}</span>
          </div>
          <div class="col-3" v-if="isStandardSheet">
            <i class="far fa-shopping-cart mr-2 fa-fw"></i> <span class="station-schedule-row-highlight">{{sheet.orderCount}} Order{{displayCountPlural(sheet.orderCount)}}</span>
          </div>
          <div class="col-3" v-if="isSplitSheet">
            <i class="far fa-border-inner mr-2 fa-fw"></i> <span class="station-schedule-row-highlight">{{sheet.splitCount}} Split{{displayCountPlural(sheet.splitCount)}}</span>
          </div>
        </div>          

        <div class="row no-gutters">
          <div class="col-6" v-if="isStandardSheet">
            <i class="far fa-info-circle mr-2 fa-fw"></i> {{allocateAreaPercent}}% Allocated
          </div>
          <div class="col-6" v-if="isSplitSheet"> 
            &nbsp;
          </div>
        </div>

        <div class="row no-gutters">
          <div class="col-6">
            <span v-if="sheet.glassLevelIsOffcut"><i class="far fa-barcode-alt mr-2 fa-fw"></i> {{sheet.glassLevelBarcode}}</span>
          </div>
          <div class="col-6 text-right station-schedule-row-label-area station-schedule-row-truncate">
            <LabelBadge labelClass="label-inventory-handcut" v-if="sheet.stockIsHandCut">HAND CUT</LabelBadge>
            <LabelBadge labelClass="label-optimiser-sheet-custom" v-if="sheet.isCustomSize">CUSTOM</LabelBadge>
            <LabelBadge labelClass="label-optimiser-sheet-offcut" v-if="sheet.glassLevelIsOffcut">OFFCUT</LabelBadge>
            <LabelBadge labelClass="label-order-urgent" v-if="sheet.urgentPanelCount > 0">Urgent</LabelBadge>
            <LabelBadge labelClass="label-order-type-nonChargeableRework" v-if="sheet.recutPanelCount > 0">RECUT</LabelBadge>
            <LabelBadge labelClass="label-order-type-nonChargeableRework" v-if="sheet.ncrPanelCount > 0">NCR</LabelBadge>
            <LabelBadge labelClass="label-optimiser-sheet-type-splitSheet" v-if="isSplitSheet">SPLIT SHEET</LabelBadge>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>


<style lang="scss" scoped>
.optimiser-row-container {
  margin: 5px;
  height: 114px;
  display: flex;
  flex-direction: row;
  color: #A0A0A0;
  cursor: pointer;
  transition: all 0.5s;   /* For transition animations */
}
.optimiser-row-contents {
  background-color: #333333;
  flex: 1.0;
  padding: 4px;
  display: flex;
  flex-direction: row;
}
.optimiser-row-thumbnail {
  width: 112px;
}
.optimiser-row-details {
  flex: 1;
}

.optimiser-row-highlight-bar {
  height: 100%;
  width: 4px;
  background-color: #4CD137;
}
.optimiser-row-details > .row {
  margin-bottom: 2px;
}
.offcut-size {
  color: #4fcc3b;
}

.optimiser-row-container .thumbnail {
  margin-top: 2px;
  margin-left: 2px;
  background-color: #505050;
  border: 1px solid #000000;
  height: 100px;
}
</style>


<script>
import moment from 'moment'
import Utils from '@/utils/utils'

import { OptimiserSheetTypes, OptimiserSheetStatusTypes, OptimiserSheetStatusTypeLookup } from '@/store/types'

export default {
  name: 'OptimiserSheetRow',
  props: {
    sheet: Object,
    colourHighlight: String
  },
  computed: {
    sideBarColour() {
      return this.colourHighlight
    },
    allocateAreaPercent() {
      if(!this.sheet)
        return 0
      if(this.sheet.statTotalArea == 0)
        return 0
      
      return Utils.roundNumber((this.sheet.statUsedArea / this.sheet.statTotalArea) * 100.0, 0)
    },
    dueDate() {
      if(!this.sheet)
        return null
      return this.sheet.dateDueDate
    },
    isOverdue() {
      if(!this.dueDate)
        return false
      return moment(this.dueDate).isBefore(moment())
    },
    isStandardSheet() {
      if(!this.sheet)
        return false
      return this.sheet.type == OptimiserSheetTypes.standard
    },
    isSplitSheet() {
      if(!this.sheet)
        return false
      return this.sheet.type == OptimiserSheetTypes.splitSheet
    }
  },
  created() {
    this.OptimiserSheetStatusTypeLookup = OptimiserSheetStatusTypeLookup
  },
  methods: {
    onClick(evt) {
      this.$emit('click', evt)
    }
  }
}
</script>